import { useState, useMemo } from "react";
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Clock,
  AlertCircle,
  Repeat
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const PRIORITY_COLORS = {
  high: "bg-red-500",
  medium: "bg-amber-500",
  low: "bg-green-500"
};

const PRIORITY_BG = {
  high: "bg-red-50 border-red-200 hover:bg-red-100",
  medium: "bg-amber-50 border-amber-200 hover:bg-amber-100",
  low: "bg-green-50 border-green-200 hover:bg-green-100"
};

export default function DeadlineCalendar({ deadlines, onSelectDeadline, onEditDeadline }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Group deadlines by date
  const deadlinesByDate = useMemo(() => {
    const map = {};
    deadlines.forEach(deadline => {
      if (deadline.date) {
        const dateKey = deadline.date.split('T')[0];
        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push(deadline);
      }
    });
    return map;
  }, [deadlines]);

  // Get calendar grid data
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= totalDays; i++) {
      const date = new Date(year, month, i);
      days.push({ date, isCurrentMonth: true });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }
    
    return days;
  }, [currentDate]);

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
  };

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const isToday = (date) => {
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  const getDeadlinesForDate = (date) => {
    const key = formatDateKey(date);
    return deadlinesByDate[key] || [];
  };

  const handleDateClick = (date, dateDeadlines) => {
    setSelectedDate(date);
    if (onSelectDeadline && dateDeadlines.length > 0) {
      onSelectDeadline(dateDeadlines);
    }
  };

  return (
    <div className="card p-4" data-testid="deadline-calendar">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-violet-600" />
          <h2 className="text-lg font-semibold text-zinc-800">
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={goToToday}
            className="h-8 text-xs"
            data-testid="calendar-today-btn"
          >
            Today
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="h-8 w-8 p-0"
            onClick={() => navigateMonth(-1)}
            data-testid="calendar-prev-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="h-8 w-8 p-0"
            onClick={() => navigateMonth(1)}
            data-testid="calendar-next-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="border rounded-lg overflow-hidden">
        {/* Days Header */}
        <div className="grid grid-cols-7 bg-zinc-50 border-b">
          {DAYS.map(day => (
            <div 
              key={day} 
              className="py-2 text-center text-xs font-medium text-zinc-500"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarData.map((dayData, index) => {
            const dateDeadlines = getDeadlinesForDate(dayData.date);
            const hasDeadlines = dateDeadlines.length > 0;
            const highPriority = dateDeadlines.some(d => d.priority === 'high');
            const dayIsToday = isToday(dayData.date);
            const dayIsSelected = isSelected(dayData.date);
            
            return (
              <TooltipProvider key={index}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={`
                        min-h-[80px] p-1 border-b border-r cursor-pointer transition-colors
                        ${!dayData.isCurrentMonth ? 'bg-zinc-50/50' : 'bg-white'}
                        ${dayIsToday ? 'bg-violet-50' : ''}
                        ${dayIsSelected ? 'ring-2 ring-violet-500 ring-inset' : ''}
                        ${hasDeadlines ? 'hover:bg-zinc-50' : 'hover:bg-zinc-25'}
                      `}
                      onClick={() => handleDateClick(dayData.date, dateDeadlines)}
                      data-testid={`calendar-day-${formatDateKey(dayData.date)}`}
                    >
                      {/* Day Number */}
                      <div className={`
                        flex items-center justify-center w-6 h-6 rounded-full text-sm mb-1
                        ${dayIsToday ? 'bg-violet-600 text-white font-semibold' : ''}
                        ${!dayData.isCurrentMonth ? 'text-zinc-300' : 'text-zinc-700'}
                      `}>
                        {dayData.date.getDate()}
                      </div>

                      {/* Deadline Indicators */}
                      {hasDeadlines && (
                        <div className="space-y-0.5">
                          {dateDeadlines.slice(0, 3).map((deadline, idx) => (
                            <div
                              key={deadline.id || idx}
                              className={`
                                text-[10px] px-1 py-0.5 rounded truncate border
                                ${PRIORITY_BG[deadline.priority] || PRIORITY_BG.medium}
                                ${deadline.completed ? 'opacity-50 line-through' : ''}
                              `}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onEditDeadline) onEditDeadline(deadline);
                              }}
                            >
                              {deadline.title.length > 12 
                                ? deadline.title.substring(0, 12) + '...' 
                                : deadline.title}
                            </div>
                          ))}
                          {dateDeadlines.length > 3 && (
                            <div className="text-[10px] text-zinc-500 px-1">
                              +{dateDeadlines.length - 3} more
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </TooltipTrigger>
                  {hasDeadlines && (
                    <TooltipContent side="right" className="max-w-xs">
                      <div className="space-y-2">
                        <div className="font-medium text-sm">
                          {dayData.date.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </div>
                        {dateDeadlines.map((deadline, idx) => (
                          <div 
                            key={deadline.id || idx}
                            className="flex items-start gap-2 text-xs"
                          >
                            <div className={`w-2 h-2 rounded-full mt-1 ${PRIORITY_COLORS[deadline.priority]}`} />
                            <div>
                              <div className="font-medium flex items-center gap-1">
                                {deadline.title}
                                {deadline.is_recurring && (
                                  <Repeat className="w-3 h-3 text-violet-500" />
                                )}
                              </div>
                              {deadline.category && (
                                <div className="text-zinc-500">{deadline.category}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-4 text-xs text-zinc-500">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span>High Priority</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span>Medium Priority</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span>Low Priority</span>
        </div>
        <div className="flex items-center gap-1">
          <Repeat className="w-3 h-3 text-violet-500" />
          <span>Recurring</span>
        </div>
      </div>

      {/* Selected Date Panel */}
      {selectedDate && (
        <div className="mt-4 pt-4 border-t">
          <h3 className="text-sm font-medium text-zinc-700 mb-2">
            {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric',
              year: 'numeric'
            })}
          </h3>
          {getDeadlinesForDate(selectedDate).length > 0 ? (
            <div className="space-y-2">
              {getDeadlinesForDate(selectedDate).map((deadline, idx) => (
                <div 
                  key={deadline.id || idx}
                  className={`
                    flex items-center justify-between p-2 rounded border cursor-pointer
                    ${PRIORITY_BG[deadline.priority] || PRIORITY_BG.medium}
                    ${deadline.completed ? 'opacity-60' : ''}
                  `}
                  onClick={() => onEditDeadline && onEditDeadline(deadline)}
                  data-testid={`selected-deadline-${deadline.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[deadline.priority]}`} />
                    <div>
                      <div className={`text-sm font-medium ${deadline.completed ? 'line-through' : ''}`}>
                        {deadline.title}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        {deadline.category && <span>{deadline.category}</span>}
                        {deadline.country_code && <span>• {deadline.country_code}</span>}
                      </div>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {deadline.is_custom ? 'Custom' : 'Regulatory'}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No deadlines on this date</p>
          )}
        </div>
      )}
    </div>
  );
}
